// Minimal client-side EXIF reader: extracts a photo's "date taken" (DateTimeOriginal)
// from a JPEG before upload. The Workers runtime can't decode images, so this runs in
// the browser and the value is sent to the server as a query param. Best-effort — any
// parse failure resolves to null (the item simply won't have a taken date).
//
// We deliberately parse only the few bytes we need (the APP1/TIFF/EXIF IFD path to tag
// 0x9003) rather than pull in an EXIF library — keeps the dependency-light rule intact.

/** Returns 'YYYY-MM-DD HH:MM:SS' from a JPEG's EXIF DateTimeOriginal (or DateTime), or null. */
export async function readExifDateTaken(file: File): Promise<string | null> {
  if (file.type !== 'image/jpeg') return null; // EXIF dates are effectively JPEG-only here
  try {
    // EXIF lives in an APP1 segment near the file start; 256KB is far more than enough.
    const buf = await file.slice(0, 256 * 1024).arrayBuffer();
    const view = new DataView(buf);
    const len = view.byteLength;
    if (len < 4 || view.getUint16(0) !== 0xffd8) return null; // not a JPEG (SOI marker)

    let offset = 2;
    while (offset + 4 <= len) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) break; // out of marker space — give up
      if (marker === 0xffda) break; // start of scan — no metadata beyond here
      const size = view.getUint16(offset + 2);
      if (size < 2) break;
      if (marker === 0xffe1) {
        // APP1 — verify the "Exif\0\0" header, then parse the TIFF block that follows.
        const seg = offset + 4;
        if (seg + 6 <= len && view.getUint32(seg) === 0x45786966 && view.getUint16(seg + 4) === 0x0000) {
          return parseExifDate(view, seg + 6);
        }
      }
      offset += 2 + size;
    }
  } catch {
    /* malformed file — best effort only */
  }
  return null;
}

function parseExifDate(view: DataView, tiffStart: number): string | null {
  const len = view.byteLength;
  if (tiffStart + 8 > len) return null;
  const le = view.getUint16(tiffStart) === 0x4949; // "II" little-endian / "MM" big-endian
  const u16 = (o: number) => view.getUint16(o, le);
  const u32 = (o: number) => view.getUint32(o, le);
  if (u16(tiffStart + 2) !== 0x002a) return null; // TIFF magic

  // Read an ASCII tag value from an IFD; ASCII >4 bytes is stored at an offset.
  const readAscii = (ifd: number, tag: number): string | null => {
    if (ifd + 2 > len) return null;
    const count = u16(ifd);
    for (let i = 0; i < count; i++) {
      const e = ifd + 2 + i * 12;
      if (e + 12 > len) break;
      if (u16(e) !== tag) continue;
      const n = u32(e + 4); // component count (string length incl. trailing NUL)
      const valOff = n <= 4 ? e + 8 : tiffStart + u32(e + 8);
      let s = '';
      for (let j = 0; j < n && valOff + j < len; j++) {
        const ch = view.getUint8(valOff + j);
        if (ch === 0) break;
        s += String.fromCharCode(ch);
      }
      return s || null;
    }
    return null;
  };
  // Read a sub-IFD pointer (LONG) from an IFD.
  const readPointer = (ifd: number, tag: number): number | null => {
    if (ifd + 2 > len) return null;
    const count = u16(ifd);
    for (let i = 0; i < count; i++) {
      const e = ifd + 2 + i * 12;
      if (e + 12 > len) break;
      if (u16(e) === tag) return tiffStart + u32(e + 8);
    }
    return null;
  };

  const ifd0 = tiffStart + u32(tiffStart + 4);
  const exifIfd = readPointer(ifd0, 0x8769); // ExifIFD pointer
  let raw = exifIfd != null ? readAscii(exifIfd, 0x9003) : null; // DateTimeOriginal
  if (!raw) raw = readAscii(ifd0, 0x0132); // fall back to DateTime (file modify)
  return normalizeExifDate(raw);
}

/** EXIF stores dates as "YYYY:MM:DD HH:MM:SS"; normalize to "YYYY-MM-DD HH:MM:SS". */
function normalizeExifDate(s: string | null): string | null {
  if (!s) return null;
  const m = s.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, Y, Mo, D, H, Mi, S] = m;
  if (Y === '0000' || Mo === '00' || D === '00') return null; // some cameras write zeroed dates
  return `${Y}-${Mo}-${D} ${H}:${Mi}:${S}`;
}
